import React, {useContext, useEffect, useRef, useState} from 'react';
import {View, Pressable, Platform} from 'react-native';
import {
  Menu,
  MenuOptions,
  MenuOption,
  MenuTrigger,
} from 'react-native-popup-menu';

import Text from '../../components/elements/Text';
import Divider from '../../components/elements/Divider';
import Button from '../../components/elements/Button';
import Popup from '../../components/elements/Popup';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES} from '../../utils/Constants';
import {fetchApi} from '../../utils/ApiHelper';
import {
  ADDRESSES,
  DELETE_ADDRESS,
  HTTP_METHODS,
  MAKE_DELETE_ADDRESS_DEFAULT,
} from '../../utils/URLConstants';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {shopContext} from '../../contexts/ShopContext';
import UI from './ui';
import CommonStyles from '../../utils/CommonStyles';
import {tID} from '../../utils/TestIdHelper';
import WebPopupMenu from '../../components/elements/WebPopupMenu';
import {
  MapPinFilledIcon,
  MapPinIcon,
  VerticalDotsIcon,
} from '../../utils/SvgIcons';

const MyAddresses = ({
  route,
  navigation,
  setAddOrEditAddress,
  addOrEditAddress,
  setMyAddressesVisibility,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const isFromCheckout = route?.params && route?.params?.isFromCheckout;

  const [addresses, setAddresses] = useState([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedAdrdress, setSelectedAdrdress] = useState(-1);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);
  const ShopC = useContext(shopContext);

  const webPopupRef = useRef();

  useEffect(() => {
    let unsubscribe;

    if (Platform.OS !== 'web') {
      unsubscribe = navigation?.addListener('focus', () => {
        getAddresses();
      });
    }

    // Return the function to unsubscribe from the event so it gets removed on unmount
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus-only data load
  }, [navigation]);

  useEffect(() => {
    if (!addOrEditAddress || isFromCheckout) getAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch intentionally keyed to add/edit panel close only
  }, [addOrEditAddress]);

  const getAddresses = () => {
    lpc?.setVisibleWithTimeout(true);
    fetchApi(HTTP_METHODS.GET, ADDRESSES)
      .then(response => {
        lpc.setVisibleWithTimeout(false);
        if (response.status === 200) {
          setAddresses(response.data.data.addresses);
        }
      })
      .catch(error => {
        // Dlog(error);
        lpc.setVisibleWithTimeout(false);
        esc.showError(error);
      });
  };

  const deleteCard = () => {
    if (selectedAdrdress === -1) {
      return;
    }
    setShowDeletePopup(false);
    const address = selectedAdrdress;
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.DELETE, DELETE_ADDRESS(address.id))
      .then(response => {
        if (response.status === 200) {
          getAddresses();
        }
      })
      .catch(error => {
        // Dlog(error);
        lpc.setVisible(false);
        esc.showError(error);
      });
  };

  const onPressNewAddress = () => {
    if (Platform.OS === 'web') {
      setAddOrEditAddress(true);
    } else {
      navigation.navigate('EditAddress');
    }
  };

  const onPressMakeDefault = item => {
    if (Platform.OS === 'web') webPopupRef.current.close();
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.PUT, MAKE_DELETE_ADDRESS_DEFAULT(item.id))
      .then(response => {
        if (response.status === 200) {
          getAddresses();
        }
      })
      .catch(error => {
        // Dlog(error);
        lpc.setVisible(false);
        esc.showError(error);
      });
  };

  const onPressEditAddress = item => {
    if (Platform.OS === 'web') {
      webPopupRef.current.close();
      setAddOrEditAddress(item);
    } else {
      navigation.navigate('EditAddress', {addressData: item});
    }
  };

  const onPressAddress = item => {
    if (isFromCheckout) {
      ShopC.setDefaultAddress(item);
      if (Platform.OS !== 'web') {
        navigation.goBack();
      } else {
        setMyAddressesVisibility(false);
      }
    } else {
      if (Platform.OS === 'web') {
        setAddOrEditAddress(item);
      } else {
        navigation.navigate('EditAddress', {addressData: item});
      }
    }
  };

  const onPressDeleteAddress = index => {
    if (Platform.OS === 'web') webPopupRef.current.close();
    setSelectedAdrdress(index);
    setShowDeletePopup(true);
  };

  const renderListItem = ({item, index}) => {
    const isDefault = item.isDefault;
    return (
      <View
        testID={tID(`MyAddresses-View-Address${index + 1}`)}
        key={index}
        style={styles.itemContainer(isDefault)}>
        <Pressable
          testID={tID(`MyAddresses-Pressable-Address${index + 1}`)}
          style={styles.listItemContainer}
          onPress={() => onPressAddress(item)}>
          <View
            testID={tID(`MyAddresses-View-AddressDetailsContainer${index + 1}`)}
            style={styles.nameContainer}>
            {isDefault ? (
              <MapPinFilledIcon style={CommonStyles.mr_20} />
            ) : (
              <MapPinIcon style={CommonStyles.mr_20} />
            )}
            <View style={CommonStyles.full_flex}>
              {isDefault && item.addressType && (
                <Text
                  testID={tID(`MyAddresses-Text-AddressName${index + 1}`)}
                  variation="Subtitle1SemiBold"
                  color={colors.textPrimary}
                  numberOfLines={1}>
                  {item.addressType.name}
                </Text>
              )}
              {!isDefault && item.addressType && (
                <Text
                  testID={tID(`MyAddresses-Text-AddressName${index + 1}`)}
                  variation="Subtitle1Regular"
                  color={colors.textPrimary}
                  numberOfLines={1}>
                  {item.addressType.name}
                </Text>
              )}
              {isDefault && (
                <Text
                  testID={tID(`MyAddresses-Text-AddressDetails${index + 1}`)}
                  variation="Text1SemiBold"
                  numberOfLines={1}
                  color={colors.textSecondary}
                  topMargin={4}>
                  {item.address1} {item.address2}
                </Text>
              )}
              {!isDefault && (
                <Text
                  testID={tID(`MyAddresses-Text-AddressDetails${index + 1}`)}
                  variation="Text1Regular"
                  numberOfLines={1}
                  color={colors.textSecondary}
                  topMargin={4}>
                  {item.address1} {item.address2}
                </Text>
              )}
              {isDefault && (
                <Text
                  testID={tID(`MyAddresses-Text-AddressDefault${index + 1}`)}
                  variation="Text1RegularItalic"
                  color={colors.textMuted}
                  topMargin={8}>
                  Selected Default
                </Text>
              )}
            </View>
          </View>
          {Platform.OS === 'web' ? (
            <WebPopupMenu ref={webPopupRef}>
              <View>
                <Text
                  testID={tID(`MyAddresses-Text-MakeDefault${index + 1}`)}
                  variation="Subtitle1Regular"
                  color={colors.textMuted}
                  style={CommonStyles.p_16}
                  onPress={() => onPressMakeDefault(item)}>
                  Make Default
                </Text>
                <Divider color={colors.border} top={0} />
                <Text
                  testID={tID(`MyAddresses-Text-EditAddress${index + 1}`)}
                  variation="Subtitle1Regular"
                  color={colors.textMuted}
                  style={CommonStyles.p_16}
                  onPress={() => onPressEditAddress(item)}>
                  Edit Address
                </Text>
                <Divider color={colors.border} top={0} />
                <Text
                  testID={tID(`MyAddresses-Text-DeleteAddress${index + 1}`)}
                  variation="Subtitle1Regular"
                  color={colors.textMuted}
                  style={CommonStyles.p_16}
                  onPress={() => onPressDeleteAddress(item)}>
                  Delete Address
                </Text>
              </View>
            </WebPopupMenu>
          ) : (
            <Menu>
              <MenuTrigger>
                <View style={styles.p10}>
                  <VerticalDotsIcon style={CommonStyles.pv_10} />
                </View>
              </MenuTrigger>
              <MenuOptions optionsContainerStyle={styles.optionContainer}>
                <MenuOption
                  onSelect={() => onPressMakeDefault(item)}
                  customStyles={{optionWrapper: styles.optionWrapper}}>
                  <Text
                    testID={tID(`MyAddresses-Text-MakeDefault${index + 1}`)}
                    variation="Subtitle1Regular"
                    color={colors.textMuted}>
                    Make Default
                  </Text>
                </MenuOption>
                <MenuOption
                  onSelect={() => onPressEditAddress(item)}
                  customStyles={{optionWrapper: styles.optionWrapper}}>
                  <Text
                    testID={tID(`MyAddresses-Text-EditAddress${index + 1}`)}
                    variation="Subtitle1Regular"
                    color={colors.textMuted}>
                    Edit Address
                  </Text>
                </MenuOption>
                <MenuOption
                  onSelect={() => onPressDeleteAddress(item)}
                  customStyles={{optionWrapper: styles.paddingVertical16}}>
                  <Text
                    testID={tID(`MyAddresses-Text-DeleteAddress${index + 1}`)}
                    variation="Subtitle1Regular"
                    color={colors.textMuted}>
                    Delete Address
                  </Text>
                </MenuOption>
              </MenuOptions>
            </Menu>
          )}
        </Pressable>
        <Divider top={0} />
      </View>
    );
  };

  const renderEmptyComponent = () => {
    return (
      <View style={styles.paddingHorizontal24}>
        <Text
          testID={tID('MyAddresses-Text-NoneSaved')}
          variation="Body1Italic"
          topMargin={16}
          color={colors.textSecondary}>
          None Saved.
        </Text>
      </View>
    );
  };

  const renderDeleteAddressConfirmation = () => {
    return (
      <Popup
        testID={tID('MyAddresses-Popup-DeleteConfirmation')}
        title={'Delete Address?'}
        visible={showDeletePopup}
        description="Are you sure you want to remove this address from your list?">
        <Button
          testID={tID('MyAddresses-Button-Cancel')}
          style={CommonStyles.full_flex}
          onPress={() => setShowDeletePopup(false)}
          title={'Cancel'}
        />
        <Button
          testID={tID('MyAddresses-Button-Delete')}
          style={styles.deleteButton}
          onPress={deleteCard}
          title={'Delete'}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
        />
      </Popup>
    );
  };

  const uiProps = {
    navigation,
    onRequestOpen: route?.params?.onRequestOpen,
    addresses,
    renderListItem,
    renderEmptyComponent,
    onPressNewAddress,
    renderDeleteAddressConfirmation,
    isFromCheckout,
  };

  return <UI {...uiProps} />;
};

export default MyAddresses;
